import React, { useState, useEffect, useRef, useCallback } from 'react';
import { 
  Play, Pause, Trash2, MoreHorizontal, Clock, 
  MessageCircle, HardDrive, Users, FileText, Plus, Check,
  Edit2, ChevronLeft, ChevronRight, GripVertical, X, Save,
  CheckSquare, Square, RotateCcw, Settings2, Pencil, RefreshCw,
  Bell, BellOff, CalendarDays, List
} from 'lucide-react';
import { toast } from 'sonner';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import { useTaskTimer } from '../contexts/TaskTimerContext';
import { useAuth, useUserId } from '../context/UserAuthContext';
import { useDemoMode } from '../context/DemoContext';
import { Calendar } from '../components/ui/calendar';

// Force the correct API URL - bypass any interceptors
const API_URL = (() => {
  // Always use the current origin for API calls
  if (typeof window !== 'undefined') {
    return window.location.origin;
  }
  return process.env.REACT_APP_BACKEND_URL || '';
})();

// Priority colors
const PRIORITY_COLORS = {
  high: { dot: 'bg-red-500', bar: 'bg-red-500' },
  medium: { dot: 'bg-yellow-500', bar: 'bg-yellow-500' },
  low: { dot: 'bg-cyan-500', bar: 'bg-cyan-500' }
};

// Format time as HH:MM:SS
const formatTime = (seconds) => {
  const hrs = Math.floor(seconds / 3600);
  const mins = Math.floor((seconds % 3600) / 60);
  const secs = seconds % 60;
  return `${hrs.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
};

// Format date
const formatDate = (date) => {
  return new Date(date).toLocaleDateString('en-US', { 
    day: 'numeric', 
    month: 'long' 
  });
};

// Format due time for display (12-hour format)
const formatDueTime = (time) => {
  if (!time) return '';
  const [hours, minutes] = time.split(':');
  const h = parseInt(hours);
  const ampm = h >= 12 ? 'PM' : 'AM';
  const h12 = h % 12 || 12;
  return `${h12}:${minutes} ${ampm}`;
};

const Tasks = () => {
  const { token, loading: authLoading } = useAuth();
  const userId = useUserId();
  const { isDemoMode, demoData, triggerViewOnlyWarning } = useDemoMode();
  const [tasks, setTasks] = useState([]);
  const [selectedTask, setSelectedTask] = useState(null);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingTask, setEditingTask] = useState(null);
  const [inlineEditing, setInlineEditing] = useState(null);
  const [inlineValue, setInlineValue] = useState('');
  const [isLoading, setIsLoading] = useState(true);

  // ── Category tabs state ──
  const [taskCategories, setTaskCategories] = useState([]);
  const [activeCategory, setActiveCategory] = useState('all');
  const [showManageCategories, setShowManageCategories] = useState(false);
  const [editingCatId, setEditingCatId] = useState(null);
  const [editingCatName, setEditingCatName] = useState('');
  const [newCatName, setNewCatName] = useState('');
  const [newCatColor, setNewCatColor] = useState('#8B5CF6');
  const [addingCategory, setAddingCategory] = useState(false);
  
  // Notifications state - now persisted in database
  const [notificationsEnabled, setNotificationsEnabled] = useState(true);
  const [notificationsLoaded, setNotificationsLoaded] = useState(false);
  
  // Schedule view mode: 'timeline' or 'calendar'
  const [scheduleViewMode, setScheduleViewMode] = useState('timeline');
  const [selectedCalendarDate, setSelectedCalendarDate] = useState(new Date());
  
  const [newTask, setNewTask] = useState({
    title: '',
    category: '',
    description: '',
    estimated_hours: 0,
    estimated_minutes: 30,
    priority: 'medium',
    due_date: '',
    due_time: '',
    recurrence: 'none',
    day_of_week: ''  // For weekly recurring tasks: 0=Sunday, 1=Monday, etc.
  });

  // Global timer context — persists even when this page is unmounted
  const { activeTimers, runningTaskIds, startTimer: globalStartTimer, pauseTimer: globalPauseTimer, toggleTimer: globalToggleTimer } = useTaskTimer();

  // Always-current tasks ref to avoid stale closures inside setInterval callbacks
  const tasksRef = useRef([]);
  useEffect(() => { tasksRef.current = tasks; }, [tasks]);

  // Fetch notification preference from database on mount
  useEffect(() => {
    const fetchNotificationPref = async () => {
      if (!userId) return;
      try {
        const response = await fetch(`${API_URL}/api/user-addons/preferences?user_id=${encodeURIComponent(userId)}`);
        if (response.ok) {
          const data = await response.json();
          const prefs = data.preferences || {};
          // Use database value if exists, otherwise default to true
          if (prefs.task_notifications_enabled !== undefined) {
            setNotificationsEnabled(prefs.task_notifications_enabled);
          }
        }
      } catch (error) {
        console.error('Failed to fetch notification preferences:', error);
      } finally {
        setNotificationsLoaded(true);
      }
    };
    
    fetchNotificationPref();
  }, [userId]);

  // Save notification preference to database when changed (only after initial load)
  useEffect(() => {
    if (!notificationsLoaded || !userId) return;
    
    const saveNotificationPref = async () => {
      try {
        await fetch(`${API_URL}/api/user-addons/preferences?user_id=${encodeURIComponent(userId)}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ task_notifications_enabled: notificationsEnabled })
        });
      } catch (error) {
        console.error('Failed to save notification preference:', error);
      }
    };
    
    saveNotificationPref();
  }, [notificationsEnabled, notificationsLoaded, userId]);

  // Toggle notifications handler
  const toggleNotifications = () => {
    const newValue = !notificationsEnabled;
    setNotificationsEnabled(newValue);
    
    // Store in localStorage for immediate sync with GlobalTaskMonitor
    localStorage.setItem('taskNotificationsEnabled', JSON.stringify(newValue));
    
    if (newValue) {
      // Request browser notification permission if enabling
      if ('Notification' in window && Notification.permission === 'default') {
        Notification.requestPermission();
      }
      toast.success('Task notifications enabled');
    } else {
      toast.info('Task notifications disabled');
    }
  };

  // Get next scheduled incomplete task sorted by due_time (for "up next" display)
  const getNextScheduledTask = useCallback((excludeTaskId = null) => {
    const now = new Date();
    const todayStr = `${now.getFullYear()}-${String(now.getMonth()+1).padStart(2,'0')}-${String(now.getDate()).padStart(2,'0')}`;
    return tasksRef.current
      .filter(t => {
        if (t.completed || !t.due_time || t.id === excludeTaskId) return false;
        // Skip future-dated tasks
        if (t.due_date) {
          const d = t.due_date.includes('T') ? t.due_date.split('T')[0] : t.due_date;
          if (d > todayStr) return false;
        }
        return true;
      })
      .sort((a, b) => a.due_time.localeCompare(b.due_time))
      .find(t => {
        const [h, m] = t.due_time.split(':');
        const taskTime = new Date();
        taskTime.setHours(parseInt(h), parseInt(m), 0, 0);
        return taskTime > now;
      }) || null;
  }, []);
  
  // Get the alarm trigger from context
  // Note: triggerAlarm is now handled by TaskTimerContext — alarms fire even off-page
  
  // Helper function to check if a task occurs on a specific date based on recurrence
  const taskOccursOnDate = useCallback((task, targetDate) => {
    if (!task.due_date) return false;
    
    // Parse the task's original due date
    const dateStr = task.due_date.includes('T') ? task.due_date.split('T')[0] : task.due_date;
    const [year, month, day] = dateStr.split('-').map(Number);
    const taskStartDate = new Date(year, month - 1, day);
    taskStartDate.setHours(0, 0, 0, 0);
    
    const target = new Date(targetDate);
    target.setHours(0, 0, 0, 0);
    
    // If target is before the task start date, it doesn't occur
    if (target < taskStartDate) return false;
    
    // If same date, always show
    if (taskStartDate.getTime() === target.getTime()) return true;
    
    // Check recurrence pattern
    const recurrence = task.recurrence || 'none';
    
    if (recurrence === 'none') {
      // Non-recurring task only shows on its due date
      return taskStartDate.getTime() === target.getTime();
    }
    
    if (recurrence === 'daily') {
      // Daily tasks occur every day from start date onwards
      return target >= taskStartDate;
    }
    
    if (recurrence === 'weekly') {
      // Weekly tasks - if day_of_week is set, use that; otherwise use original day
      if (target < taskStartDate) return false;
      
      // Check if task has a specific day_of_week set
      if (task.day_of_week !== null && task.day_of_week !== undefined && task.day_of_week !== '') {
        const targetDayOfWeek = target.getDay(); // 0=Sunday, 1=Monday, etc.
        return targetDayOfWeek === parseInt(task.day_of_week);
      }
      
      // Fallback to same day of week as start date
      const daysDiff = Math.floor((target - taskStartDate) / (1000 * 60 * 60 * 24));
      return daysDiff % 7 === 0;
    }
    
    if (recurrence === 'monthly') {
      // Monthly tasks occur on the same day of month
      if (target < taskStartDate) return false;
      const startDay = taskStartDate.getDate();
      const targetDay = target.getDate();
      // Handle months with fewer days
      const lastDayOfTargetMonth = new Date(target.getFullYear(), target.getMonth() + 1, 0).getDate();
      if (startDay > lastDayOfTargetMonth) {
        // If original day doesn't exist in this month, use last day
        return targetDay === lastDayOfTargetMonth;
      }
      return targetDay === startDay && target >= taskStartDate;
    }
    
    return false;
  }, []);
  
  // Get all tasks that occur on a specific date (including recurring)
  const getTasksForDate = useCallback((targetDate) => {
    return tasks.filter(task => taskOccursOnDate(task, targetDate));
  }, [tasks, taskOccursOnDate]);
  
  // Helper to get auth headers
  const getAuthHeaders = useCallback(() => {
    const headers = { 'Content-Type': 'application/json' };
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
    return headers;
  }, [token]);

  // Ref to track whether initial task selection has been done (avoids including
  // selectedTask in fetchTasks deps, which caused a re-fetch loop that reset isRunning)
  const initialTaskSelectedRef = useRef(false);

  // Fetch tasks
  // NOTE: selectedTask is intentionally NOT in the dependency array to prevent
  // a feedback loop: startTimer() → setSelectedTask() → fetchTasks() → isRunning reset
  const fetchTasks = useCallback(async () => {
    // Use demo data if in demo mode
    if (isDemoMode && demoData?.tasks) {
      const demoTasks = demoData.tasks.map((task, idx) => ({
        id: task.id,
        _id: task.id,
        title: task.title,
        name: task.title,
        description: task.notes || '',
        priority: task.priority || 'medium',
        status: task.status === 'completed' ? 'completed' : (task.status === 'in_progress' ? 'in_progress' : 'pending'),
        due_date: task.due_date,
        category: task.category || 'General',
        project: task.category || 'General',
        order: idx,
        estimatedHours: 1,
        time_spent: 0,
        isRunning: false,
        completed: task.status === 'completed'
      }));
      setTasks(demoTasks);
      if (demoTasks.length > 0 && !initialTaskSelectedRef.current) {
        initialTaskSelectedRef.current = true;
        setSelectedTask(demoTasks[0]);
      }
      setIsLoading(false);
      console.log(`✅ Demo mode: Loaded ${demoTasks.length} demo tasks`);
      return;
    }
    
    if (!userId) return;
    setIsLoading(true);
    try {
      const apiUrl = `${window.location.origin}/api/tasks?user_id=${encodeURIComponent(userId)}`;
      const response = await window.fetch(apiUrl);
      if (response.ok) {
        const data = await response.json();
        // Map backend fields to frontend-expected fields
        // Preserve isRunning state from current tasks so running timers survive re-fetch
        setTasks(prev => {
          const prevMap = new Map(prev.map(t => [t.id, t]));
          const enrichedTasks = data.map(task => ({
            ...task,
            name: task.title, // Map title to name for display
            project: task.category,
            estimatedHours: (task.estimated_hours || 0) + (task.estimated_minutes || 0) / 60,
            time_spent: 0,
            isRunning: prevMap.get(task.id)?.isRunning || false, // preserve running state
            completed: task.status === 'completed'
          }));
          // Set initial selected task only once
          if (enrichedTasks.length > 0 && !initialTaskSelectedRef.current) {
            initialTaskSelectedRef.current = true;
            setSelectedTask(enrichedTasks[0]);
          }
          return enrichedTasks;
        });
      }
    } catch (error) {
      console.error('Failed to fetch tasks:', error);
    } finally {
      setIsLoading(false);
    }
  }, [userId, isDemoMode, demoData]); // removed selectedTask from deps to prevent re-fetch loop

  // ── Fetch + manage task categories ──
  const fetchCategories = useCallback(async () => {
    if (!userId) return;
    try {
      const res = await window.fetch(`${window.location.origin}/api/task-categories?user_id=${encodeURIComponent(userId)}`);
      if (res.ok) {
        const data = await res.json();
        setTaskCategories(data.categories || []);
      }
    } catch (e) { console.error('Failed to load categories:', e); }
  }, [userId]);

  const handleAddCategory = async () => {
    if (!newCatName.trim()) return;
    try {
      const res = await window.fetch(`${window.location.origin}/api/task-categories?user_id=${encodeURIComponent(userId)}`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: newCatName.trim(), color: newCatColor })
      });
      if (res.ok) {
        const data = await res.json();
        setTaskCategories(prev => [...prev, data.category]);
        setNewCatName(''); setNewCatColor('#8B5CF6'); setAddingCategory(false);
        toast.success(`Category "${data.category.name}" added`);
      }
    } catch { toast.error('Failed to add category'); }
  };

  const handleRenameCategory = async (catId) => {
    if (!editingCatName.trim()) return;
    try {
      const res = await window.fetch(`${window.location.origin}/api/task-categories/${catId}?user_id=${encodeURIComponent(userId)}`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: editingCatName.trim() })
      });
      if (res.ok) {
        const data = await res.json();
        setTaskCategories(prev => prev.map(c => c.id === catId ? data.category : c));
        setEditingCatId(null); setEditingCatName('');
      }
    } catch { toast.error('Failed to rename category'); }
  };

  const handleDeleteCategory = async (catId) => {
    try {
      const res = await window.fetch(`${window.location.origin}/api/task-categories/${catId}?user_id=${encodeURIComponent(userId)}`, { method: 'DELETE' });
      if (res.ok) {
        setTaskCategories(prev => prev.filter(c => c.id !== catId));
        if (activeCategory === catId) setActiveCategory('all');
        toast.success('Category removed');
      }
    } catch { toast.error('Failed to delete category'); }
  };

  const handleReorderCategories = async (newOrder) => {
    setTaskCategories(newOrder);
    try {
      await window.fetch(`${window.location.origin}/api/task-categories/reorder?user_id=${encodeURIComponent(userId)}`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ordered_ids: newOrder.map(c => c.id) })
      });
    } catch { console.error('Reorder save failed'); }
  };

  useEffect(() => {
    if (userId) {
      fetchTasks();
      fetchCategories();
    } else if (!authLoading) {
      setIsLoading(false);
    }
  }, [fetchTasks, fetchCategories, userId, authLoading]);

  // Mark task as complete and announce the next upcoming task
  const markTaskComplete = useCallback(async (taskId) => {
    if (!userId) return;
    try {
      const response = await window.fetch(`${window.location.origin}/api/tasks/${taskId}?user_id=${encodeURIComponent(userId)}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: 'completed', completed: true })
      });
      
      if (response.ok) {
        setTasks(prev => prev.map(t => 
          t.id === taskId ? { ...t, completed: true, isRunning: false, status: 'completed' } : t
        ));
        toast.success('Task marked as complete!');

        // Announce the next upcoming task
        const next = getNextScheduledTask(taskId);
        if (next) {
          const fmt = (time) => {
            const [h, m] = time.split(':');
            const hour = parseInt(h);
            return `${hour % 12 || 12}:${m} ${hour >= 12 ? 'PM' : 'AM'}`;
          };
          setTimeout(() => {
            toast.info(`Next up: "${next.title || next.name}" starts at ${fmt(next.due_time)}`, { duration: 5000 });
          }, 1500);
        }
      }
    } catch (error) {
      console.error('Failed to mark task complete:', error);
      toast.error('Failed to mark task complete');
    }
  }, [userId, getNextScheduledTask]);

  // ── Local wrappers around the global timer context ───────────────────────
  // The global context keeps timers alive even when this page is unmounted.
  const startTimer = useCallback((taskId) => {
    const task = tasksRef.current.find(t => t.id === taskId);
    if (!task) return;

    setSelectedTask(task);
    const estimatedSeconds = ((task.estimated_hours || 0) * 3600) + ((task.estimated_minutes || 0) * 60);

    globalStartTimer(taskId, task, estimatedSeconds);
  }, [globalStartTimer]);

  const pauseTimer = useCallback((taskId) => {
    globalPauseTimer(taskId);
  }, [globalPauseTimer]);

  const toggleTimer = useCallback((taskId) => {
    if (runningTaskIds.has(taskId)) {
      pauseTimer(taskId);
    } else {
      startTimer(taskId);
    }
  }, [runningTaskIds, startTimer, pauseTimer]);

  // Refresh tasks list whenever any timer completes (tasksUpdated event from context)
  useEffect(() => {
    const handleTasksUpdated = () => fetchTasks();
    window.addEventListener('tasksUpdated', handleTasksUpdated);
    return () => window.removeEventListener('tasksUpdated', handleTasksUpdated);
  }, [fetchTasks]);

  // Immediately check the checkbox when a timer completes (taskTimerComplete event)
  useEffect(() => {
    const handleTimerComplete = (e) => {
      const { taskId } = e.detail || {};
      if (!taskId) return;
      // Optimistically flip the checkbox right away — DB is already being updated by the context
      setTasks(prev => prev.map(t =>
        t.id === taskId ? { ...t, completed: true, isRunning: false, status: 'completed' } : t
      ));
    };
    window.addEventListener('taskTimerComplete', handleTimerComplete);
    return () => window.removeEventListener('taskTimerComplete', handleTimerComplete);
  }, []);

  // Note: Timer cleanup is handled by TaskTimerContext (global, persists across navigation)

  // Add new task
  const handleAddTask = async () => {
    if (!newTask.title.trim()) {
      toast.error('Please enter a task title');
      return;
    }

    if (!userId) {
      toast.error('Please log in to add tasks');
      return;
    }

    try {
      // Use absolute URL to bypass any interceptors
      const apiUrl = `${window.location.origin}/api/tasks?user_id=${encodeURIComponent(userId)}`;
      console.log('Adding task to:', apiUrl);
      
      const response = await window.fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          title: newTask.title,
          description: newTask.description,
          category: newTask.category || null,
          priority: newTask.priority,
          status: 'pending',
          due_date: newTask.due_date || null,
          due_time: newTask.due_time || null,
          estimated_hours: newTask.estimated_hours || 0,
          estimated_minutes: newTask.estimated_minutes || 0,
          recurrence: newTask.recurrence !== 'none' ? newTask.recurrence : null,
          day_of_week: newTask.day_of_week !== '' ? parseInt(newTask.day_of_week) : null,
          user_id: userId
        })
      });

      console.log('Response status:', response.status);
      
      if (response.ok) {
        const task = await response.json();
        // Add frontend-specific fields for timer
        const totalMinutes = (task.estimated_hours || 0) * 60 + (task.estimated_minutes || 0);
        const enrichedTask = {
          ...task,
          name: task.title, // Map title to name for display
          estimatedHours: totalMinutes / 60,
          estimated_hours: task.estimated_hours,
          estimated_minutes: task.estimated_minutes,
          time_spent: 0,
          isRunning: false,
          completed: task.status === 'completed'
        };
        setTasks(prev => [enrichedTask, ...prev]);
        setShowAddModal(false);
        setNewTask({ 
          title: '', 
          category: '', 
          description: '', 
          estimated_hours: 0, 
          estimated_minutes: 30, 
          priority: 'medium', 
          due_date: '', 
          due_time: '',
          recurrence: 'none',
          day_of_week: ''
        });
        // Notify Dashboard to refresh its task list immediately
        window.dispatchEvent(new CustomEvent('tasksUpdated'));
        toast.success('Task added!');
      } else {
        const err = await response.text();
        console.error('Failed to add task:', err);
        toast.error('Failed to add task');
      }
    } catch (error) {
      console.error('Error adding task:', error);
      toast.error('Failed to add task');
    }
  };

  // Delete task
  const handleDeleteTask = async (taskId) => {
    // Block in demo mode
    if (isDemoMode) {
      triggerViewOnlyWarning();
      return;
    }
    
    try {
      await window.fetch(`${window.location.origin}/api/tasks/${taskId}?user_id=${encodeURIComponent(userId)}`, { 
        method: 'DELETE'
      });
      setTasks(prev => prev.filter(t => t.id !== taskId));
      if (selectedTask?.id === taskId) {
        setSelectedTask(tasks.find(t => t.id !== taskId) || null);
      }
      pauseTimer(taskId);
      toast.success('Task deleted');
    } catch (error) {
      toast.error('Failed to delete task');
    }
  };

  // Toggle task completion
  const toggleComplete = async (taskId) => {
    const task = tasks.find(t => t.id === taskId);
    if (!task) return;

    const newCompleted = !task.completed;
    const newStatus = newCompleted ? 'completed' : 'pending';

    // Optimistic update — update UI immediately so there's no flicker
    setTasks(prev => prev.map(t =>
      t.id === taskId ? { ...t, completed: newCompleted, status: newStatus } : t
    ));

    try {
      await window.fetch(`${window.location.origin}/api/tasks/${taskId}?user_id=${encodeURIComponent(userId)}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        // Send BOTH completed + status so fetchTasks re-hydration stays consistent
        body: JSON.stringify({ completed: newCompleted, status: newStatus })
      });
    } catch (error) {
      // Revert optimistic update on failure
      setTasks(prev => prev.map(t =>
        t.id === taskId ? { ...t, completed: task.completed, status: task.status } : t
      ));
      toast.error('Failed to update task');
    }
  };

  // ── Auto-reset daily tasks at midnight ──
  const resetDailyTasks = useCallback(async () => {
    if (!userId) return;
    try {
      const res = await window.fetch(
        `${window.location.origin}/api/tasks/reset-daily?user_id=${encodeURIComponent(userId)}`,
        { method: 'POST' }
      );
      if (res.ok) {
        const data = await res.json();
        if (data.reset_count > 0) {
          // Reset both completed + status so re-hydration stays consistent
          setTasks(prev => prev.map(t =>
            t.recurrence === 'daily' ? { ...t, completed: false, status: 'pending' } : t
          ));
          toast.success(`${data.reset_count} daily task${data.reset_count > 1 ? 's' : ''} reset for today`);
        }
        localStorage.setItem('last_daily_reset', new Date().toDateString());
      }
    } catch (err) {
      console.error('Daily reset failed:', err);
    }
  }, [userId]);

  // Run daily reset check on mount and when userId changes
  useEffect(() => {
    if (!userId) return;
    const lastReset = localStorage.getItem('last_daily_reset');
    const today = new Date().toDateString();
    if (lastReset !== today) {
      resetDailyTasks();
    }
  }, [userId, resetDailyTasks]);

  // Check at midnight every day while the page is open
  useEffect(() => {
    const msUntilMidnight = () => {
      const now = new Date();
      const midnight = new Date(now);
      midnight.setHours(24, 0, 0, 0);
      return midnight - now;
    };

    let timeout;
    const scheduleMidnightReset = () => {
      timeout = setTimeout(() => {
        resetDailyTasks();
        scheduleMidnightReset(); // reschedule for next midnight
      }, msUntilMidnight());
    };
    scheduleMidnightReset();
    return () => clearTimeout(timeout);
  }, [resetDailyTasks]);

  // ── Bulk complete/uncomplete all visible tasks ──
  const allCompleted = tasks.length > 0 && tasks.every(t => t.completed);
  const handleBulkComplete = async (completed) => {
    if (!userId || tasks.length === 0) return;
    try {
      const res = await window.fetch(
        `${window.location.origin}/api/tasks/bulk-complete?user_id=${encodeURIComponent(userId)}&completed=${completed}`,
        { method: 'POST', headers: { 'Content-Type': 'application/json' } }
      );
      if (res.ok) {
        setTasks(prev => prev.map(t => ({ ...t, completed })));
        toast.success(completed ? 'All tasks marked complete' : 'All tasks unchecked');
      }
    } catch (err) {
      toast.error('Failed to update tasks');
    }
  };

  // Open edit modal
  const openEditModal = (task) => {
    // Ensure due_date is in YYYY-MM-DD format for the date input
    let formattedDate = '';
    if (task.due_date) {
      // If it's an ISO string, extract just the date part
      if (task.due_date.includes('T')) {
        formattedDate = task.due_date.split('T')[0];
      } else {
        formattedDate = task.due_date;
      }
    }
    
    setEditingTask({
      ...task,
      estimated_hours: task.estimated_hours || Math.floor(task.estimatedHours || 0),
      estimated_minutes: task.estimated_minutes || Math.round(((task.estimatedHours || 0) % 1) * 60),
      due_date: formattedDate,
      due_time: task.due_time || '',
      recurrence: task.recurrence || 'none',
      day_of_week: task.day_of_week !== null && task.day_of_week !== undefined ? task.day_of_week.toString() : ''
    });
    setShowEditModal(true);
  };

  // Start inline editing for a field in the Task # panel
  const startInlineEdit = (field, value) => {
    setInlineEditing(field);
    setInlineValue(value || '');
  };

  // Save inline edit
  const saveInlineEdit = async (field) => {
    if (!selectedTask || !userId) return;
    
    try {
      const updateData = { [field]: inlineValue };
      
      // If editing title, also update name
      if (field === 'title') {
        updateData.name = inlineValue;
      }
      
      const response = await window.fetch(`${window.location.origin}/api/tasks/${selectedTask.id}?user_id=${encodeURIComponent(userId)}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(updateData)
      });

      if (response.ok) {
        const updatedTask = await response.json();
        setTasks(prev => prev.map(t => 
          t.id === selectedTask.id 
            ? { ...t, ...updatedTask, name: updatedTask.title || t.name } 
            : t
        ));
        setSelectedTask(prev => ({ ...prev, ...updatedTask, name: updatedTask.title || prev.name }));
        toast.success('Updated!');
      }
    } catch (error) {
      toast.error('Failed to update');
    }
    
    setInlineEditing(null);
    setInlineValue('');
  };

  // Cancel inline edit
  const cancelInlineEdit = () => {
    setInlineEditing(null);
    setInlineValue('');
  };

  // Save edited task
  const handleSaveEdit = async () => {
    if (!editingTask || !userId) return;
    
    try {
      const response = await window.fetch(`${window.location.origin}/api/tasks/${editingTask.id}?user_id=${encodeURIComponent(userId)}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          title: editingTask.title,
          description: editingTask.description,
          category: editingTask.category,
          priority: editingTask.priority,
          due_date: editingTask.due_date || null,
          due_time: editingTask.due_time || null,
          estimated_hours: editingTask.estimated_hours || 0,
          estimated_minutes: editingTask.estimated_minutes || 0,
          recurrence: editingTask.recurrence !== 'none' ? editingTask.recurrence : null,
          day_of_week: editingTask.day_of_week !== '' ? parseInt(editingTask.day_of_week) : null
        })
      });

      if (response.ok) {
        const updatedTask = await response.json();
        const totalMinutes = (updatedTask.estimated_hours || 0) * 60 + (updatedTask.estimated_minutes || 0);
        setTasks(prev => prev.map(t => 
          t.id === editingTask.id 
            ? { 
                ...t, 
                ...updatedTask,
                name: updatedTask.title,
                estimatedHours: totalMinutes / 60
              } 
            : t
        ));
        setShowEditModal(false);
        setEditingTask(null);
        toast.success('Task updated!');
      } else {
        toast.error('Failed to update task');
      }
    } catch (error) {
      toast.error('Failed to update task');
    }
  };

  // Handle drag and drop reorder
  const handleDragEnd = async (result) => {
    if (!result.destination) return;
    
    const items = Array.from(tasks);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);
    
    // Update local state immediately for smooth UX
    setTasks(items);
    
    // Persist order to backend
    try {
      const taskIds = items.map(t => t.id);
      const response = await window.fetch(`${window.location.origin}/api/tasks/reorder`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ task_ids: taskIds })
      });
      
      if (response.ok) {
        toast.success('Task order saved');
      } else {
        toast.error('Failed to save order');
      }
    } catch (error) {
      console.error('Failed to save task order:', error);
      toast.error('Failed to save order');
    }
  };

  // Get progress percentage
  const getProgress = (task) => {
    const estimated = (task.estimatedHours || 2) * 3600;
    const spent = activeTimers[task.id] || task.time_spent || 0;
    return Math.min((spent / estimated) * 100, 100);
  };

  // Generate hours for full day (12 AM to 11 PM)
  const reminderHours = [];
  for (let i = 0; i <= 23; i++) {
    reminderHours.push(i);
  }
  
  // Format hour for display
  const formatHour = (hour) => {
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const h12 = hour % 12 || 12;
    return `${h12}${ampm}`;
  };
  
  // Get current hour
  const currentHour = new Date().getHours();

  // Show loading state while auth is loading
  if (authLoading) {
    return (
      <div className="min-h-screen bg-black text-white p-6 flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-purple-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-400">Loading tasks...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black text-white p-3 md:p-6">
      <div className="max-w-[1600px] mx-auto">
        <div className="flex flex-col lg:flex-row gap-4 md:gap-6">
          {/* Left Panel - Task List */}
          <div className="flex-1 min-w-0">
            {/* Header */}
            <div className="mb-4 md:mb-6">
              <h1 className="text-2xl md:text-3xl font-bold text-white mb-1">Tasks</h1>
            </div>

            {/* Date & Add Button */}
            <div className="flex items-center justify-between gap-3 mb-4">
              <div className="flex items-center gap-2 text-gray-300 text-sm md:text-base min-w-0">
                <span className="truncate">{formatDate(currentDate)}</span>
              </div>
              <button
                onClick={() => {
                  if (isDemoMode) {
                    triggerViewOnlyWarning();
                    return;
                  }
                  setShowAddModal(true);
                }}
                className="flex items-center gap-1.5 md:gap-2 px-3 md:px-4 py-2 bg-purple-600 hover:bg-purple-700 rounded-lg transition-colors text-sm md:text-base flex-shrink-0"
                data-testid="add-task-btn"
              >
                <Plus size={16} className="md:hidden" />
                <Plus size={18} className="hidden md:block" />
                <span>Add Task</span>
              </button>
            </div>

            {/* Category Tabs */}
            <div className="mb-4">
              <div className="flex items-center gap-1 overflow-x-auto pb-1 scrollbar-hide">
                {/* All tab */}
                <button
                  onClick={() => setActiveCategory('all')}
                  className={`flex-shrink-0 px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${
                    activeCategory === 'all'
                      ? 'bg-white/20 text-white shadow'
                      : 'text-gray-400 hover:text-white hover:bg-white/10'
                  }`}
                  data-testid="tab-all"
                >
                  All
                </button>
                {taskCategories.map(cat => (
                  <button
                    key={cat.id}
                    onClick={() => setActiveCategory(cat.id)}
                    className={`flex-shrink-0 px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${
                      activeCategory === cat.id
                        ? 'text-white shadow'
                        : 'text-gray-400 hover:text-white hover:bg-white/10'
                    }`}
                    style={activeCategory === cat.id ? { backgroundColor: cat.color + '33', color: cat.color, borderColor: cat.color + '66', border: '1px solid' } : {}}
                    data-testid={`tab-${cat.id}`}
                  >
                    {cat.name}
                  </button>
                ))}
                <button
                  onClick={() => setShowManageCategories(true)}
                  className="flex-shrink-0 ml-1 p-1.5 text-gray-600 hover:text-gray-300 transition-colors rounded-lg hover:bg-white/5"
                  title="Manage categories"
                  data-testid="manage-categories-btn"
                >
                  <Settings2 size={15} />
                </button>
                <button
                  onClick={toggleNotifications}
                  className={`flex-shrink-0 ml-1 p-1.5 transition-colors rounded-lg hover:bg-white/5 ${
                    notificationsEnabled 
                      ? 'text-cyan-400 hover:text-cyan-300' 
                      : 'text-gray-600 hover:text-gray-300'
                  }`}
                  title={notificationsEnabled ? 'Notifications ON - Click to disable' : 'Notifications OFF - Click to enable'}
                  data-testid="notifications-toggle-btn"
                >
                  {notificationsEnabled ? <Bell size={15} /> : <BellOff size={15} />}
                </button>
              </div>
            </div>

            {/* Bulk Actions Bar */}
            {tasks.length > 0 && (
              <div className="flex items-center justify-between mb-4 px-3 py-2 bg-white/5 rounded-lg border border-white/10">
                <div className="flex items-center gap-3">
                  <button
                    onClick={() => handleBulkComplete(!allCompleted)}
                    className="flex items-center gap-2 text-sm text-gray-300 hover:text-white transition-colors"
                    data-testid="bulk-check-btn"
                    title={allCompleted ? 'Uncheck all tasks' : 'Check all tasks'}
                  >
                    {allCompleted
                      ? <CheckSquare size={16} className="text-purple-400" />
                      : <Square size={16} className="text-gray-500" />
                    }
                    <span>{allCompleted ? 'Uncheck All' : 'Check All'}</span>
                  </button>
                  <span className="text-gray-600 text-xs">
                    {tasks.filter(t => t.completed).length}/{tasks.length} done
                  </span>
                </div>
                <button
                  onClick={resetDailyTasks}
                  className="flex items-center gap-1.5 text-xs text-gray-500 hover:text-cyan-400 transition-colors"
                  data-testid="reset-daily-btn"
                  title="Reset all daily tasks"
                >
                  <RotateCcw size={13} />
                  Reset Daily
                </button>
              </div>
            )}

            {/* Task List — scrollable, shows 11 rows before requiring scroll */}
            <div className="overflow-y-auto pr-1" style={{ maxHeight: '792px' }}
              data-testid="task-list-scroller">
            <DragDropContext onDragEnd={handleDragEnd}>
              <Droppable droppableId="tasks" isDropDisabled={false} isCombineEnabled={false}>
                {(provided) => (
                  <div 
                    className="space-y-3"
                    {...provided.droppableProps}
                    ref={provided.innerRef}
                  >
                    {tasks
                      .filter(task => activeCategory === 'all' || (task.category || '').toLowerCase() === (taskCategories.find(c => c.id === activeCategory)?.name || '').toLowerCase())
                      .map((task, index) => {
                      const colors = PRIORITY_COLORS[task.priority] || PRIORITY_COLORS.medium;
                      const timeSpent = activeTimers[task.id] || task.time_spent || 0;
                      const progress = getProgress(task);
                      
                      return (
                        <Draggable key={task.id} draggableId={task.id} index={index} isDragDisabled={false}>
                          {(provided, snapshot) => (
                            <div
                              ref={provided.innerRef}
                              {...provided.draggableProps}
                              onClick={() => setSelectedTask(task)}
                              className={`
                                relative p-3 md:p-4 rounded-xl border transition-all duration-200 cursor-pointer
                                ${selectedTask?.id === task.id 
                                  ? 'border-gray-500 bg-gray-900/50' 
                                  : 'border-gray-800 hover:border-gray-600 hover:bg-gray-900/30'}
                                ${snapshot.isDragging ? 'shadow-lg shadow-purple-500/20 border-purple-500' : ''}
                              `}
                              data-testid={`task-row-${task.id}`}
                            >
                              <div className="flex flex-wrap items-center gap-2 md:gap-4">
                                {/* Drag Handle — hidden on mobile to save space */}
                                <div 
                                  {...provided.dragHandleProps}
                                  className="hidden md:block cursor-grab active:cursor-grabbing text-gray-600 hover:text-gray-400"
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  <GripVertical size={18} />
                                </div>

                                {/* Checkbox */}
                                <button
                                  onClick={(e) => { e.stopPropagation(); toggleComplete(task.id); }}
                                  className={`
                                    w-6 h-6 rounded border-2 flex items-center justify-center transition-colors flex-shrink-0
                                    ${task.completed 
                                      ? 'bg-green-500 border-green-500' 
                                      : 'border-gray-600 hover:border-gray-400'}
                                  `}
                                  data-testid={`task-checkbox-${task.id}`}
                                >
                                  {task.completed && <Check size={14} />}
                                </button>

                                {/* Priority Dot */}
                                <div className={`w-2 h-2 rounded-full flex-shrink-0 ${colors.dot}`} />

                                {/* Task Name & Badges */}
                                <div className="flex-1 min-w-0 flex flex-wrap items-center gap-1.5">
                                  <span className={`font-medium ${task.completed ? 'line-through text-gray-500' : 'text-white'}`}>
                                    {task.name}
                                  </span>
                                  {/* Estimated Time Badge */}
                                  {(task.estimated_hours > 0 || task.estimated_minutes > 0 || task.estimatedHours) && (
                                    <span className="px-2 py-0.5 bg-red-500/20 text-red-400 text-xs rounded whitespace-nowrap">
                                      {task.estimated_hours > 0 ? `${task.estimated_hours}h ` : ''}
                                      {task.estimated_minutes > 0 ? `${task.estimated_minutes}m` :
                                        (!task.estimated_hours && task.estimatedHours ? `${task.estimatedHours}h` : '')}
                                    </span>
                                  )}
                                  {/* Category Badge — shows custom category with its color */}
                                  {task.category && (() => {
                                    const cat = taskCategories.find(c => c.name.toLowerCase() === (task.category || '').toLowerCase());
                                    return cat ? (
                                      <span
                                        className="px-2 py-0.5 text-xs rounded whitespace-nowrap font-medium"
                                        style={{ backgroundColor: (cat.color || '#8B5CF6') + '30', color: cat.color || '#8B5CF6' }}
                                      >
                                        {cat.name}
                                      </span>
                                    ) : (
                                      <span className="px-2 py-0.5 bg-gray-700/50 text-gray-400 text-xs rounded whitespace-nowrap">
                                        {task.category}
                                      </span>
                                    );
                                  })()}
                                  {/* Recurrence Badge — only shows if task repeats */}
                                  {task.recurrence && task.recurrence !== 'none' && (
                                    <span className="px-2 py-0.5 bg-blue-500/15 text-blue-400 text-xs rounded whitespace-nowrap flex items-center gap-1">
                                      <RefreshCw size={9} />
                                      {task.recurrence.charAt(0).toUpperCase() + task.recurrence.slice(1)}
                                    </span>
                                  )}
                                </div>

                                {/* Due Date & Time - Fixed width column on desktop, auto on mobile */}
                                <div className="flex items-center gap-2 text-gray-400 text-xs md:w-36 flex-shrink-0 justify-end ml-auto md:ml-0">
                                  {task.due_date && (
                                    <span className="inline-flex items-center gap-1">
                                      {(() => {
                                        const dateStr = task.due_date.includes('T') ? task.due_date.split('T')[0] : task.due_date;
                                        const [year, month, day] = dateStr.split('-').map(Number);
                                        const date = new Date(year, month - 1, day);
                                        return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
                                      })()}
                                    </span>
                                  )}
                                  {task.due_time ? (
                                    <span className="inline-flex items-center gap-1 text-cyan-400 font-medium">
                                      <Clock size={12} />
                                      {formatDueTime(task.due_time)}
                                    </span>
                                  ) : !task.completed && (
                                    <button
                                      onClick={(e) => { e.stopPropagation(); openEditModal(task); }}
                                      className="inline-flex items-center gap-1 px-2 py-0.5 bg-amber-500/20 text-amber-400 rounded text-xs hover:bg-amber-500/30 transition-colors"
                                      title="Set a start time for auto-timer"
                                      data-testid={`task-set-time-${task.id}`}
                                    >
                                      <Clock size={10} />
                                      Set time
                                    </button>
                                  )}
                                </div>

                      {/* Timer Controls */}
                      <div className="flex items-center gap-2 md:gap-3 ml-auto md:ml-0">
                        <button
                          onClick={(e) => { e.stopPropagation(); toggleTimer(task.id); }}
                          className={`
                            w-8 h-8 rounded-full flex items-center justify-center transition-colors flex-shrink-0
                            ${runningTaskIds.has(task.id) 
                              ? 'bg-red-500 hover:bg-red-600' 
                              : 'bg-gray-700 hover:bg-gray-600'}
                          `}
                          data-testid={`task-timer-${task.id}`}
                        >
                          {runningTaskIds.has(task.id) ? <Pause size={14} /> : <Play size={14} className="ml-0.5" />}
                        </button>

                        {/* Progress Bar — hidden on mobile to save space */}
                        <div className="hidden md:block w-24 h-1.5 bg-gray-800 rounded-full overflow-hidden">
                          <div 
                            className={`h-full ${colors.bar} transition-all duration-300`}
                            style={{ width: `${progress}%` }}
                          />
                        </div>

                        {/* Time Display */}
                        <span className="text-gray-400 font-mono text-xs md:text-sm md:w-20">
                          {formatTime(timeSpent)}
                        </span>

                        {/* Edit Button */}
                        <button
                          onClick={(e) => { e.stopPropagation(); openEditModal(task); }}
                          className="p-1.5 md:p-2 text-gray-500 hover:text-blue-500 hover:bg-blue-500/10 rounded-lg transition-colors"
                          data-testid={`task-edit-${task.id}`}
                        >
                          <Edit2 size={14} className="md:hidden" />
                          <Edit2 size={16} className="hidden md:block" />
                        </button>

                        {/* Delete Button */}
                        <button
                          onClick={(e) => { e.stopPropagation(); handleDeleteTask(task.id); }}
                          className="p-1.5 md:p-2 text-gray-500 hover:text-red-500 hover:bg-red-500/10 rounded-lg transition-colors"
                          data-testid={`task-delete-${task.id}`}
                        >
                          <Trash2 size={14} className="md:hidden" />
                          <Trash2 size={16} className="hidden md:block" />
                        </button>

                        {/* "More" button — desktop only */}
                        <button className="hidden md:block p-2 text-gray-500 hover:text-white hover:bg-gray-800 rounded-lg transition-colors">
                          <MoreHorizontal size={16} />
                        </button>
                                  </div>
                                </div>
                              </div>
                            )}
                          </Draggable>
                        );
                      })}
                      {provided.placeholder}
                    </div>
                  )}
                </Droppable>
              </DragDropContext>
            </div>

            {tasks.length === 0 && !isLoading && (
                <div className="text-center py-12 text-gray-500" data-testid="tasks-empty-state">
                  {userId ? (
                    <p>No tasks yet. Click "Add Task" to create one.</p>
                  ) : (
                    <p>Please log in to view and manage your tasks.</p>
                  )}
                </div>
              )}
              
              {isLoading && (
                <div className="text-center py-12 text-gray-500">
                  <div className="w-8 h-8 border-4 border-purple-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                  <p>Loading tasks...</p>
                </div>
              )}

            {/* Reminder Section */}
            <div className="mt-6 md:mt-8">
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-4">
                <div className="flex items-center gap-2 min-w-0">
                  <h2 className="text-lg md:text-xl font-bold text-white truncate">
                    {scheduleViewMode === 'calendar' ? 'Task Calendar' : "Today's Schedule"}
                  </h2>
                  <Edit2 size={16} className="text-gray-500 flex-shrink-0" />
                </div>
                <div className="flex items-center gap-2 md:gap-3 flex-wrap">
                  {/* View Toggle */}
                  <div className="flex items-center bg-gray-800 rounded-lg p-1">
                    <button
                      onClick={() => setScheduleViewMode('timeline')}
                      className={`flex items-center gap-1.5 px-2 md:px-3 py-1 md:py-1.5 rounded-md text-xs font-medium transition-colors ${
                        scheduleViewMode === 'timeline' 
                          ? 'bg-purple-600 text-white' 
                          : 'text-gray-400 hover:text-white'
                      }`}
                    >
                      <List size={14} />
                      Timeline
                    </button>
                    <button
                      onClick={() => setScheduleViewMode('calendar')}
                      className={`flex items-center gap-1.5 px-2 md:px-3 py-1 md:py-1.5 rounded-md text-xs font-medium transition-colors ${
                        scheduleViewMode === 'calendar' 
                          ? 'bg-purple-600 text-white' 
                          : 'text-gray-400 hover:text-white'
                      }`}
                    >
                      <CalendarDays size={14} />
                      Calendar
                    </button>
                  </div>
                  <div className="text-xs md:text-sm text-gray-400 truncate">
                    {scheduleViewMode === 'calendar' 
                      ? selectedCalendarDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })
                      : currentDate.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })
                    }
                  </div>
                </div>
              </div>

              {/* Calendar View */}
              {scheduleViewMode === 'calendar' && (
                <div className="border border-gray-800 rounded-xl p-3 md:p-6 bg-gray-900/20">
                  <div className="flex flex-col xl:flex-row gap-4 md:gap-8">
                    {/* Calendar Picker - Extra Large */}
                    <div className="flex-shrink-0">
                      <Calendar
                        mode="single"
                        selected={selectedCalendarDate}
                        onSelect={(date) => date && setSelectedCalendarDate(date)}
                        className="rounded-xl border border-gray-700 bg-gray-900 p-6"
                        classNames={{
                          months: "flex flex-col space-y-6",
                          month: "space-y-6",
                          caption: "flex justify-center pt-2 relative items-center mb-4",
                          caption_label: "text-xl font-semibold text-white",
                          nav: "space-x-2 flex items-center",
                          nav_button: "h-10 w-10 bg-gray-800 hover:bg-gray-700 rounded-lg flex items-center justify-center text-white border-0 opacity-80 hover:opacity-100 transition-all",
                          nav_button_previous: "absolute left-2",
                          nav_button_next: "absolute right-2",
                          table: "w-full border-collapse",
                          head_row: "flex mb-2",
                          head_cell: "text-gray-400 rounded-md w-14 h-10 font-semibold text-sm flex items-center justify-center",
                          row: "flex w-full",
                          cell: "relative p-1 text-center text-base focus-within:relative focus-within:z-20 [&:has([aria-selected])]:bg-transparent",
                          day: "h-12 w-14 p-0 font-medium rounded-lg hover:bg-gray-700 transition-colors text-white flex items-center justify-center",
                          day_selected: "!bg-purple-600 text-white hover:!bg-purple-500",
                          day_today: "!bg-gray-700 text-white font-bold",
                          day_outside: "text-gray-600 opacity-50",
                          day_disabled: "text-gray-700 opacity-30",
                        }}
                        components={{
                          IconLeft: () => <ChevronLeft className="h-5 w-5" />,
                          IconRight: () => <ChevronRight className="h-5 w-5" />,
                          DayContent: ({ date }) => {
                            // Get all tasks for this date including recurring ones
                            const tasksOnDay = getTasksForDate(date);
                            const incompleteTasks = tasksOnDay.filter(t => !t.completed);
                            const completedTasks = tasksOnDay.filter(t => t.completed);
                            const totalTasks = tasksOnDay.length;
                            
                            // Check for recurring tasks
                            const hasRecurring = tasksOnDay.some(t => t.recurrence && t.recurrence !== 'none');
                            
                            return (
                              <div className="relative w-full h-full flex flex-col items-center justify-center">
                                <span className="text-base">{date.getDate()}</span>
                                {totalTasks > 0 && (
                                  <div className="absolute bottom-0 left-1/2 transform -translate-x-1/2 flex items-center gap-0.5">
                                    {incompleteTasks.length > 0 && (
                                      <span className="flex items-center">
                                        {incompleteTasks.slice(0, 2).map((task, i) => {
                                          const colors = PRIORITY_COLORS[task.priority] || PRIORITY_COLORS.medium;
                                          // Add a ring for recurring tasks
                                          const isRecurring = task.recurrence && task.recurrence !== 'none';
                                          return (
                                            <span 
                                              key={i} 
                                              className={`w-2 h-2 rounded-full ${colors.dot} mx-0.5 ${isRecurring ? 'ring-1 ring-white/50' : ''}`}
                                              title={isRecurring ? `${task.recurrence} task` : ''}
                                            />
                                          );
                                        })}
                                        {incompleteTasks.length > 2 && (
                                          <span className="text-[10px] text-purple-400 font-bold ml-0.5">+{incompleteTasks.length - 2}</span>
                                        )}
                                      </span>
                                    )}
                                    {completedTasks.length > 0 && incompleteTasks.length === 0 && (
                                      <span className="w-2 h-2 rounded-full bg-green-500" title={`${completedTasks.length} completed`} />
                                    )}
                                  </div>
                                )}
                                {/* Small recurring indicator */}
                                {hasRecurring && (
                                  <span className="absolute top-0 right-0 w-1.5 h-1.5 rounded-full bg-cyan-400" title="Has recurring tasks" />
                                )}
                              </div>
                            );
                          }
                        }}
                      />
                      
                      {/* Legend */}
                      <div className="flex flex-wrap items-center justify-center gap-4 mt-4 text-xs text-gray-400">
                        <div className="flex items-center gap-2">
                          <span className="w-2 h-2 rounded-full bg-red-500" />
                          <span>High</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="w-2 h-2 rounded-full bg-yellow-500" />
                          <span>Medium</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="w-2 h-2 rounded-full bg-blue-500" />
                          <span>Low</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="w-2 h-2 rounded-full bg-green-500" />
                          <span>Done</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="w-1.5 h-1.5 rounded-full bg-cyan-400" />
                          <span>Recurring</span>
                        </div>
                      </div>
                    </div>
                    
                    {/* Tasks for Selected Date */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between mb-4">
                        <h3 className="text-lg font-semibold text-white">
                          {selectedCalendarDate.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}
                        </h3>
                        <span className="text-sm text-gray-400">
                          {getTasksForDate(selectedCalendarDate).length} task(s)
                        </span>
                      </div>
                      <div className="space-y-3 max-h-[400px] overflow-y-auto pr-2">
                        {getTasksForDate(selectedCalendarDate)
                          .sort((a, b) => (a.due_time || '23:59').localeCompare(b.due_time || '23:59'))
                          .map(task => {
                            const colors = PRIORITY_COLORS[task.priority] || PRIORITY_COLORS.medium;
                            const isRecurring = task.recurrence && task.recurrence !== 'none';
                            return (
                              <div
                                key={task.id}
                                onClick={() => setSelectedTask(task)}
                                className={`
                                  flex items-center gap-4 p-4 rounded-xl cursor-pointer transition-all
                                  ${task.completed ? 'bg-gray-800/30 opacity-60' : 'bg-gray-800/50 hover:bg-gray-800'}
                                  ${selectedTask?.id === task.id ? 'ring-2 ring-purple-500' : ''}
                                  ${runningTaskIds.has(task.id) ? 'ring-2 ring-green-500 animate-pulse' : ''}
                                `}
                              >
                                <div className={`w-3 h-3 rounded-full ${colors.dot} flex-shrink-0`} />
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center gap-2">
                                    <p className={`text-base font-medium ${task.completed ? 'line-through text-gray-500' : 'text-white'}`}>
                                      {task.title || task.name}
                                    </p>
                                    {isRecurring && (
                                      <span className="text-xs px-2 py-0.5 rounded-full bg-cyan-500/20 text-cyan-400 flex items-center gap-1">
                                        <RefreshCw size={10} />
                                        {task.recurrence}
                                      </span>
                                    )}
                                  </div>
                                  <div className="flex items-center gap-3 mt-1">
                                    {task.due_time && (
                                      <span className="text-sm text-cyan-400 flex items-center gap-1">
                                        <Clock size={12} />
                                        {formatDueTime(task.due_time)}
                                      </span>
                                    )}
                                    {(task.estimated_hours > 0 || task.estimated_minutes > 0) && (
                                      <span className="text-sm text-gray-500">
                                        Duration: {task.estimated_hours > 0 ? `${task.estimated_hours}h ` : ''}{task.estimated_minutes > 0 ? `${task.estimated_minutes}m` : ''}
                                      </span>
                                    )}
                                    {task.category && (
                                      <span className="text-xs px-2 py-0.5 rounded-full bg-gray-700 text-gray-300">
                                        {task.category}
                                      </span>
                                    )}
                                  </div>
                                </div>
                                {task.completed ? (
                                  <Check size={20} className="text-green-500 flex-shrink-0" />
                                ) : runningTaskIds.has(task.id) ? (
                                  <div className="w-5 h-5 rounded-full border-2 border-green-500 border-t-transparent animate-spin flex-shrink-0" />
                                ) : (
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleStartTimer(task);
                                    }}
                                    className="p-2 rounded-lg bg-purple-600/20 hover:bg-purple-600/40 text-purple-400 transition-colors"
                                    title="Start timer"
                                  >
                                    <Play size={16} />
                                  </button>
                                )}
                              </div>
                            );
                          })}
                        {getTasksForDate(selectedCalendarDate).length === 0 && (
                          <div className="flex flex-col items-center justify-center h-48 text-gray-500 bg-gray-800/20 rounded-xl">
                            <CalendarDays size={48} className="mb-3 opacity-50" />
                            <p className="text-base mb-1">No tasks scheduled</p>
                            <p className="text-sm text-gray-600 mb-4">for {selectedCalendarDate.toLocaleDateString('en-US', { month: 'long', day: 'numeric' })}</p>
                            <button 
                              onClick={() => {
                                setNewTask(prev => ({
                                  ...prev,
                                  due_date: selectedCalendarDate.toISOString().split('T')[0]
                                }));
                                setShowAddModal(true);
                              }}
                              className="flex items-center gap-2 px-4 py-2 bg-purple-600 hover:bg-purple-700 rounded-lg text-white text-sm transition-colors"
                            >
                              <Plus size={16} />
                              Add a task for this day
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Hourly Timeline (original view) */}
              {scheduleViewMode === 'timeline' && (
              <div className="relative border border-gray-800 rounded-xl p-4 bg-gray-900/20">
                {/* Hour Labels — scrollable so all 24 fit */}
                <div className="flex items-center gap-0 mb-4 overflow-x-auto pb-1 scrollbar-thin scrollbar-track-transparent scrollbar-thumb-gray-700">
                  {reminderHours.map((hour, idx) => {
                    const isCurrentHour = hour === currentHour;
                    const isPast = hour < currentHour;
                    return (
                      <div 
                        key={idx}
                        className={`
                          flex-shrink-0 text-xs px-1.5 py-1 rounded whitespace-nowrap w-[46px] text-center
                          ${isCurrentHour ? 'text-red-400 font-bold bg-red-500/20' : isPast ? 'text-gray-600' : 'text-gray-400'}
                        `}
                      >
                        {formatHour(hour)}
                      </div>
                    );
                  })}
                </div>

                {/* Current Time Indicator */}
                <div 
                  className="absolute top-14 bottom-4 w-0.5 bg-red-500 z-10"
                  style={{ 
                    left: `calc(${(currentHour / 23) * 100}% + 16px)`
                  }}
                />

                {/* Timeline Tasks */}
                <div className="space-y-3 relative" style={{ minHeight: `${Math.max(100, tasks.filter(t => t.due_time && !t.completed).length * 44)}px` }}>
                  {tasks
                    .filter(task => task.due_time && !task.completed)
                    .map((task, idx) => {
                      const colors = PRIORITY_COLORS[task.priority] || PRIORITY_COLORS.medium;
                      
                      // Parse task due time to get hour position
                      let taskHour = 9; // Default to 9 AM
                      if (task.due_time) {
                        const [hours] = task.due_time.split(':');
                        taskHour = parseInt(hours);
                      }
                      
                      // Calculate position across full 24h (0 = 12AM, 23 = 11PM)
                      const leftPercent = Math.max(0, Math.min(85, (taskHour / 23) * 100));
                      
                      // Width based on estimated time
                      const estimatedHours = (task.estimated_hours || 0) + ((task.estimated_minutes || 30) / 60);
                      const widthPercent = Math.min(100 - leftPercent - 2, Math.max(10, (estimatedHours / 23) * 100));
                      
                      return (
                        <div 
                          key={task.id}
                          className="relative h-8"
                        >
                          <div 
                            className={`
                              absolute h-full rounded-lg flex items-center px-3 gap-2 text-xs cursor-pointer overflow-hidden
                              ${colors.bar} bg-opacity-30 hover:bg-opacity-50 transition-all
                              ${runningTaskIds.has(task.id) ? 'ring-2 ring-green-500 animate-pulse' : ''}
                            `}
                            style={{ 
                              left: `${leftPercent}%`, 
                              width: `${Math.max(widthPercent, 12)}%`,
                              maxWidth: `calc(100% - ${leftPercent}% - 8px)`
                            }}
                            onClick={() => setSelectedTask(task)}
                          >
                            <div className={`w-2 h-2 rounded-full ${colors.dot} flex-shrink-0`} />
                            <span className="text-white truncate font-medium flex-1">{task.title || task.name}</span>
                            <span className="text-gray-300 flex-shrink-0 text-[10px]">
                              {formatDueTime(task.due_time)}
                            </span>
                          </div>
                        </div>
                      );
                    })}
                  
                  {/* Empty state */}
                  {tasks.filter(task => task.due_time && !task.completed).length === 0 && (
                    <div className="flex items-center justify-center h-20 text-gray-500 text-sm">
                      No scheduled tasks for today. Add a due time to see tasks here.
                    </div>
                  )}
                </div>
              </div>
              )}
            </div>
          </div>

          {/* Right Panel - Time Tracking */}
          <div className="w-full lg:w-80 lg:flex-shrink-0">
            {/* Timer Card */}
            <div className="border border-gray-800 rounded-2xl p-4 md:p-6 bg-gray-900/30 hover:border-gray-600 transition-colors">
              <p className="text-gray-400 text-sm mb-1">Time Tracking</p>
              {selectedTask && (
                <p className="text-xs text-cyan-400 mb-2 truncate flex items-center gap-1">
                  {tasks.find(t => t.id === selectedTask?.id) && runningTaskIds.has(selectedTask?.id) && <span className="w-1.5 h-1.5 rounded-full bg-green-400 animate-pulse inline-block" />}
                  {selectedTask.title || selectedTask.name}
                </p>
              )}
              {/* Total time across ALL active timers */}
              <div className="text-3xl md:text-5xl font-bold text-white font-mono mb-1">
                {formatTime(Object.values(activeTimers).reduce((sum, v) => sum + v, 0))}
              </div>
              <p className="text-xs text-gray-500 mb-4">Total session time</p>
              {selectedTask && (
                <p className="text-xs text-gray-400 font-mono mb-4">
                  This task: {formatTime(activeTimers[selectedTask.id] || selectedTask.time_spent || 0)}
                </p>
              )}

              {/* Timer Controls — single toggle button */}
              <div className="flex items-center justify-center gap-4 mb-6">
                {(() => {
                  const isRunning = selectedTask && runningTaskIds.has(selectedTask.id);
                  return (
                    <button
                      onClick={() => selectedTask && toggleTimer(selectedTask.id)}
                      className={`w-16 h-16 rounded-full flex items-center justify-center transition-colors shadow-lg ${
                        isRunning
                          ? 'bg-gradient-to-br from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 shadow-orange-500/30'
                          : 'bg-gradient-to-br from-red-500 to-pink-500 hover:from-red-600 hover:to-pink-600 shadow-red-500/30'
                      }`}
                      data-testid="main-play-pause-btn"
                    >
                      {isRunning ? <Pause size={24} /> : <Play size={24} className="ml-1" />}
                    </button>
                  );
                })()}
              </div>

              {/* Circular Progress */}
              <div className="flex justify-center mb-6">
                <div className="relative w-20 h-20">
                  <svg className="w-full h-full transform -rotate-90">
                    <circle
                      cx="40"
                      cy="40"
                      r="36"
                      stroke="currentColor"
                      strokeWidth="6"
                      fill="none"
                      className="text-gray-800"
                    />
                    <circle
                      cx="40"
                      cy="40"
                      r="36"
                      stroke="currentColor"
                      strokeWidth="6"
                      fill="none"
                      strokeDasharray={`${2 * Math.PI * 36}`}
                      strokeDashoffset={`${2 * Math.PI * 36 * (1 - (selectedTask ? getProgress(selectedTask) / 100 : 0))}`}
                      className="text-red-500 transition-all duration-300"
                      strokeLinecap="round"
                    />
                  </svg>
                </div>
              </div>
            </div>

            {/* Next Up Card - shows next scheduled task */}
            {(() => {
              const nextTask = getNextScheduledTask(selectedTask?.id);
              if (!nextTask) return null;
              const fmt = (t) => {
                const [h, m] = t.split(':');
                const hour = parseInt(h);
                return `${hour % 12 || 12}:${m} ${hour >= 12 ? 'PM' : 'AM'}`;
              };
              const [h, m] = nextTask.due_time.split(':');
              const taskTime = new Date();
              taskTime.setHours(parseInt(h), parseInt(m), 0, 0);
              const diffMin = Math.max(0, Math.round((taskTime - new Date()) / 60000));
              return (
                <div className="mt-3 border border-cyan-900/50 rounded-2xl p-4 bg-cyan-900/10">
                  <p className="text-gray-400 text-xs mb-2 flex items-center gap-1.5">
                    <ChevronRight size={12} className="text-cyan-400" />
                    Up Next
                  </p>
                  <p className="text-white font-medium text-sm leading-snug mb-2 truncate">
                    {nextTask.title || nextTask.name}
                  </p>
                  <div className="flex items-center justify-between">
                    <span className="text-cyan-400 text-xs font-medium">{fmt(nextTask.due_time)}</span>
                    {diffMin > 0 && (
                      <span className="text-gray-400 text-xs">in {diffMin}m</span>
                    )}
                    {diffMin === 0 && (
                      <span className="text-green-400 text-xs animate-pulse font-medium">Starting now</span>
                    )}
                  </div>
                  {!nextTask.due_time && (
                    <button
                      onClick={() => openEditModal(nextTask)}
                      className="mt-2 w-full text-xs py-1 rounded bg-amber-500/20 text-amber-400 hover:bg-amber-500/30 transition-colors"
                    >
                      Set start time
                    </button>
                  )}
                </div>
              );
            })()}

            {/* Selected Task Details */}
            {selectedTask && (
              <div className="mt-4 border border-gray-800 rounded-2xl p-4 bg-gray-900/30 hover:border-gray-600 transition-colors max-h-[400px] overflow-y-auto">
                <div className="flex items-center gap-2 mb-4">
                  <div className={`w-2 h-2 rounded-full ${PRIORITY_COLORS[selectedTask.priority]?.dot || 'bg-orange-500'}`} />
                  <span className="text-white font-medium">Task #{tasks.indexOf(selectedTask) + 1}</span>
                  <span className="px-2 py-0.5 bg-green-500/20 text-green-400 text-xs rounded">
                    {((selectedTask.estimated_hours || 0) + (selectedTask.estimated_minutes || 0) / 60).toFixed(1)}h
                  </span>
                </div>

                <div className="space-y-4">
                  {/* Task Title - Editable */}
                  <div>
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-gray-500 text-sm">Task</span>
                      <button 
                        onClick={() => startInlineEdit('title', selectedTask.title || selectedTask.name)}
                        className="text-gray-500 hover:text-white text-xs flex items-center gap-1"
                      >
                        <Edit2 size={12} /> Edit
                      </button>
                    </div>
                    {inlineEditing === 'title' ? (
                      <div className="flex gap-2">
                        <input
                          type="text"
                          value={inlineValue}
                          onChange={(e) => setInlineValue(e.target.value)}
                          onKeyDown={(e) => e.key === 'Enter' && saveInlineEdit('title')}
                          className="flex-1 px-2 py-1 bg-gray-800 border border-gray-600 rounded text-white text-sm focus:outline-none focus:ring-1 focus:ring-purple-500"
                          autoFocus
                        />
                        <button onClick={() => saveInlineEdit('title')} className="text-green-400 hover:text-green-300">
                          <Check size={16} />
                        </button>
                        <button onClick={cancelInlineEdit} className="text-red-400 hover:text-red-300">
                          <X size={16} />
                        </button>
                      </div>
                    ) : (
                      <p className="text-white font-medium">{selectedTask.title || selectedTask.name}</p>
                    )}
                  </div>

                  {/* Project - Editable */}
                  <div>
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-gray-500 text-sm">Project</span>
                      <button 
                        onClick={() => startInlineEdit('project', selectedTask.project)}
                        className="text-gray-500 hover:text-white text-xs flex items-center gap-1"
                      >
                        <Edit2 size={12} /> Edit
                      </button>
                    </div>
                    {inlineEditing === 'project' ? (
                      <div className="flex gap-2">
                        <input
                          type="text"
                          value={inlineValue}
                          onChange={(e) => setInlineValue(e.target.value)}
                          onKeyDown={(e) => e.key === 'Enter' && saveInlineEdit('project')}
                          placeholder="Enter project name"
                          className="flex-1 px-2 py-1 bg-gray-800 border border-gray-600 rounded text-white text-sm focus:outline-none focus:ring-1 focus:ring-purple-500"
                          autoFocus
                        />
                        <button onClick={() => saveInlineEdit('project')} className="text-green-400 hover:text-green-300">
                          <Check size={16} />
                        </button>
                        <button onClick={cancelInlineEdit} className="text-red-400 hover:text-red-300">
                          <X size={16} />
                        </button>
                      </div>
                    ) : (
                      <p className="text-white">{selectedTask.project || 'No project'}</p>
                    )}
                  </div>

                  {/* Description - Editable */}
                  <div>
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-gray-500 text-sm">Description</span>
                      <button 
                        onClick={() => startInlineEdit('description', selectedTask.description)}
                        className="text-gray-500 hover:text-white text-xs flex items-center gap-1"
                      >
                        <Edit2 size={12} /> Edit
                      </button>
                    </div>
                    {inlineEditing === 'description' ? (
                      <div className="flex flex-col gap-2">
                        <textarea
                          value={inlineValue}
                          onChange={(e) => setInlineValue(e.target.value)}
                          placeholder="Enter description"
                          rows={3}
                          className="w-full px-2 py-1 bg-gray-800 border border-gray-600 rounded text-white text-sm focus:outline-none focus:ring-1 focus:ring-purple-500 resize-none"
                          autoFocus
                        />
                        <div className="flex gap-2 justify-end">
                          <button onClick={() => saveInlineEdit('description')} className="text-green-400 hover:text-green-300">
                            <Check size={16} />
                          </button>
                          <button onClick={cancelInlineEdit} className="text-red-400 hover:text-red-300">
                            <X size={16} />
                          </button>
                        </div>
                      </div>
                    ) : (
                      <p className="text-white text-sm">{selectedTask.description || 'No description'}</p>
                    )}
                  </div>

                  {/* Due Time - Editable */}
                  <div>
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-gray-500 text-sm">Due Time</span>
                      <button 
                        onClick={() => startInlineEdit('due_time', selectedTask.due_time)}
                        className="text-gray-500 hover:text-white text-xs flex items-center gap-1"
                      >
                        <Edit2 size={12} /> Edit
                      </button>
                    </div>
                    {inlineEditing === 'due_time' ? (
                      <div className="flex gap-2">
                        <input
                          type="time"
                          value={inlineValue}
                          onChange={(e) => setInlineValue(e.target.value)}
                          className="flex-1 px-2 py-1 bg-gray-800 border border-gray-600 rounded text-white text-sm focus:outline-none focus:ring-1 focus:ring-purple-500"
                          autoFocus
                        />
                        <button onClick={() => saveInlineEdit('due_time')} className="text-green-400 hover:text-green-300">
                          <Check size={16} />
                        </button>
                        <button onClick={cancelInlineEdit} className="text-red-400 hover:text-red-300">
                          <X size={16} />
                        </button>
                      </div>
                    ) : (
                      <div className="flex items-center gap-2 text-white">
                        <Clock size={16} />
                        <span>{selectedTask.due_time ? formatDueTime(selectedTask.due_time) : 'Not set'}</span>
                      </div>
                    )}
                  </div>

                  {/* Due Date */}
                  <div className="flex items-center gap-2 text-white">
                    <span>{selectedTask.due_date ? (() => {
                      const dateStr = selectedTask.due_date.includes('T') ? selectedTask.due_date.split('T')[0] : selectedTask.due_date;
                      const [year, month, day] = dateStr.split('-').map(Number);
                      const date = new Date(year, month - 1, day);
                      return date.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
                    })() : formatDate(selectedTask.created_at)}</span>
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="flex items-center justify-between mt-6 pt-4 border-t border-gray-800">
                  <button 
                    onClick={() => openEditModal(selectedTask)}
                    className="flex flex-col items-center gap-1 p-2 text-gray-500 hover:text-white transition-colors"
                  >
                    <div className="w-10 h-10 rounded-full bg-gray-800 flex items-center justify-center">
                      <Edit2 size={18} />
                    </div>
                    <span className="text-xs">Edit</span>
                  </button>
                  <button className="flex flex-col items-center gap-1 p-2 text-gray-500 hover:text-white transition-colors">
                    <div className="w-10 h-10 rounded-full bg-gray-800 flex items-center justify-center">
                      <HardDrive size={18} />
                    </div>
                    <span className="text-xs">Storage</span>
                  </button>
                  <button className="flex flex-col items-center gap-1 p-2 text-gray-500 hover:text-white transition-colors">
                    <div className="w-10 h-10 rounded-full bg-gray-800 flex items-center justify-center">
                      <Users size={18} />
                    </div>
                    <span className="text-xs">Team</span>
                  </button>
                  <button className="flex flex-col items-center gap-1 p-2 text-gray-500 hover:text-white transition-colors">
                    <div className="w-10 h-10 rounded-full bg-gray-800 flex items-center justify-center">
                      <FileText size={18} />
                    </div>
                    <span className="text-xs">Notes</span>
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Manage Categories Modal */}
      {showManageCategories && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4"
          onClick={() => setShowManageCategories(false)}>
          <div className="bg-gray-900 border border-gray-700 rounded-2xl w-full max-w-md p-6"
            onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-5">
              <h2 className="text-lg font-bold text-white flex items-center gap-2">
                <Settings2 size={18} className="text-purple-400" />
                Manage Task Categories
              </h2>
              <button onClick={() => setShowManageCategories(false)} className="text-gray-400 hover:text-white">
                <X size={20} />
              </button>
            </div>

            <p className="text-xs text-gray-500 mb-4">Drag to reorder · Click pencil to rename · Click × to delete</p>

            {/* Draggable category list */}
            <DragDropContext onDragEnd={({ source, destination }) => {
              if (!destination || destination.index === source.index) return;
              const reordered = Array.from(taskCategories);
              const [moved] = reordered.splice(source.index, 1);
              reordered.splice(destination.index, 0, moved);
              handleReorderCategories(reordered);
            }}>
              <Droppable droppableId="cat-manage">
                {(provided) => (
                  <div className="space-y-2 mb-4" {...provided.droppableProps} ref={provided.innerRef}>
                    {taskCategories.map((cat, idx) => (
                      <Draggable key={cat.id} draggableId={cat.id} index={idx}>
                        {(provided, snapshot) => (
                          <div
                            ref={provided.innerRef}
                            {...provided.draggableProps}
                            className={`flex items-center gap-3 p-3 rounded-xl border transition-all ${
                              snapshot.isDragging
                                ? 'bg-gray-700 border-purple-500 shadow-lg'
                                : 'bg-gray-800/60 border-gray-700'
                            }`}
                            data-testid={`cat-row-${cat.id}`}
                          >
                            <span {...provided.dragHandleProps} className="text-gray-600 hover:text-gray-300 cursor-grab">
                              <GripVertical size={16} />
                            </span>
                            {/* Color dot */}
                            <div className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: cat.color }} />
                            {/* Name or inline rename */}
                            {editingCatId === cat.id ? (
                              <input
                                autoFocus
                                value={editingCatName}
                                onChange={e => setEditingCatName(e.target.value)}
                                onKeyDown={e => { if (e.key === 'Enter') handleRenameCategory(cat.id); if (e.key === 'Escape') { setEditingCatId(null); } }}
                                className="flex-1 px-2 py-0.5 bg-gray-700 border border-purple-500 rounded text-white text-sm focus:outline-none"
                                data-testid={`cat-name-input-${cat.id}`}
                              />
                            ) : (
                              <span className="flex-1 text-sm text-white">{cat.name}</span>
                            )}
                            {/* Actions */}
                            <div className="flex items-center gap-1 ml-auto">
                              {editingCatId === cat.id ? (
                                <>
                                  <button onClick={() => handleRenameCategory(cat.id)}
                                    className="p-1 text-green-400 hover:text-green-300" data-testid={`save-cat-${cat.id}`}>
                                    <Check size={14} />
                                  </button>
                                  <button onClick={() => setEditingCatId(null)}
                                    className="p-1 text-gray-500 hover:text-white">
                                    <X size={14} />
                                  </button>
                                </>
                              ) : (
                                <>
                                  <button onClick={() => { setEditingCatId(cat.id); setEditingCatName(cat.name); }}
                                    className="p-1 text-gray-500 hover:text-blue-400 transition-colors" data-testid={`edit-cat-${cat.id}`}>
                                    <Pencil size={13} />
                                  </button>
                                  <button onClick={() => handleDeleteCategory(cat.id)}
                                    className="p-1 text-gray-500 hover:text-red-400 transition-colors" data-testid={`delete-cat-${cat.id}`}>
                                    <X size={14} />
                                  </button>
                                </>
                              )}
                            </div>
                          </div>
                        )}
                      </Draggable>
                    ))}
                    {provided.placeholder}
                  </div>
                )}
              </Droppable>
            </DragDropContext>

            {/* Add new category */}
            {addingCategory ? (
              <div className="flex gap-2 items-center">
                <input
                  autoFocus
                  type="color"
                  value={newCatColor}
                  onChange={e => setNewCatColor(e.target.value)}
                  className="w-8 h-8 rounded cursor-pointer border-0 bg-transparent"
                />
                <input
                  value={newCatName}
                  onChange={e => setNewCatName(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter') handleAddCategory(); if (e.key === 'Escape') setAddingCategory(false); }}
                  placeholder="Category name..."
                  className="flex-1 px-3 py-2 bg-gray-800 border border-purple-500 rounded-lg text-white text-sm focus:outline-none"
                  data-testid="new-cat-name-input"
                />
                <button onClick={handleAddCategory}
                  className="px-3 py-2 bg-purple-600 hover:bg-purple-700 text-white text-sm rounded-lg"
                  data-testid="confirm-add-cat-btn">
                  Add
                </button>
                <button onClick={() => setAddingCategory(false)} className="p-2 text-gray-500 hover:text-white">
                  <X size={16} />
                </button>
              </div>
            ) : (
              <button
                onClick={() => setAddingCategory(true)}
                className="w-full py-2.5 border border-dashed border-gray-600 hover:border-purple-500 rounded-xl text-sm text-gray-400 hover:text-white transition-colors flex items-center justify-center gap-2"
                data-testid="add-category-btn"
              >
                <Plus size={15} /> Add Category
              </button>
            )}
          </div>
        </div>
      )}

      {/* Add Task Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50" onClick={() => setShowAddModal(false)}>
          <div className="bg-gray-900 border border-gray-700 rounded-2xl w-[500px] p-6" onClick={e => e.stopPropagation()}>
            <h2 className="text-2xl font-bold text-white mb-6">Add New Task</h2>
            
            <div className="space-y-4">
              <div>
                <label className="block text-gray-400 text-sm mb-1">Task Title *</label>
                <input
                  type="text"
                  value={newTask.title}
                  onChange={e => setNewTask(prev => ({ ...prev, title: e.target.value }))}
                  placeholder="Enter task title"
                  className="w-full px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-purple-500"
                  data-testid="new-task-name"
                />
              </div>

              <div>
                <label className="block text-gray-400 text-sm mb-2">Category</label>
                <div className="flex flex-wrap gap-2" data-testid="new-task-category-selector">
                  <button
                    type="button"
                    onClick={() => setNewTask(prev => ({ ...prev, category: '' }))}
                    className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-all ${
                      !newTask.category
                        ? 'bg-white/20 border-white/50 text-white'
                        : 'border-white/10 text-gray-500 hover:border-white/30 hover:text-gray-300'
                    }`}
                  >
                    None
                  </button>
                  {taskCategories.map(cat => (
                    <button
                      key={cat.id}
                      type="button"
                      onClick={() => setNewTask(prev => ({ ...prev, category: cat.name }))}
                      className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium border transition-all ${
                        newTask.category === cat.name
                          ? 'text-white border-transparent'
                          : 'border-white/10 text-gray-400 hover:border-white/30 hover:text-white'
                      }`}
                      style={newTask.category === cat.name
                        ? { backgroundColor: cat.color || '#8B5CF6', borderColor: cat.color || '#8B5CF6' }
                        : {}}
                      data-testid={`new-task-cat-${cat.id}`}
                    >
                      <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: cat.color || '#8B5CF6' }} />
                      {cat.name}
                    </button>
                  ))}
                  {taskCategories.length === 0 && (
                    <span className="text-xs text-gray-600 italic">No categories yet — create some in Manage Categories</span>
                  )}
                </div>
              </div>

              <div>
                <label className="block text-gray-400 text-sm mb-1">Description</label>
                <textarea
                  value={newTask.description}
                  onChange={e => setNewTask(prev => ({ ...prev, description: e.target.value }))}
                  placeholder="Enter description"
                  rows={3}
                  className="w-full px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-purple-500 resize-none"
                  data-testid="new-task-description"
                />
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-gray-400 text-sm mb-1">Est. Hours</label>
                  <input
                    type="number"
                    value={newTask.estimated_hours}
                    onChange={e => setNewTask(prev => ({ ...prev, estimated_hours: parseInt(e.target.value) || 0 }))}
                    min={0}
                    max={100}
                    className="w-full px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-purple-500"
                    data-testid="new-task-hours"
                  />
                </div>

                <div>
                  <label className="block text-gray-400 text-sm mb-1">Est. Minutes</label>
                  <input
                    type="number"
                    value={newTask.estimated_minutes}
                    onChange={e => setNewTask(prev => ({ ...prev, estimated_minutes: parseInt(e.target.value) || 0 }))}
                    min={0}
                    max={59}
                    className="w-full px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-purple-500"
                    data-testid="new-task-minutes"
                  />
                </div>

                <div>
                  <label className="block text-gray-400 text-sm mb-1">Priority</label>
                  <select
                    value={newTask.priority}
                    onChange={e => setNewTask(prev => ({ ...prev, priority: e.target.value }))}
                    className="w-full px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-purple-500"
                    data-testid="new-task-priority"
                  >
                    <option value="high">High</option>
                    <option value="medium">Medium</option>
                    <option value="low">Low</option>
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-3 gap-4 mt-4">
                <div>
                  <label className="block text-gray-400 text-sm mb-1">Due Date</label>
                  <input
                    type="date"
                    value={newTask.due_date}
                    onChange={e => setNewTask(prev => ({ ...prev, due_date: e.target.value }))}
                    className="w-full px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-purple-500"
                    data-testid="new-task-due-date"
                  />
                </div>

                <div>
                  <label className="block text-gray-400 text-sm mb-1">Time</label>
                  <input
                    type="time"
                    value={newTask.due_time}
                    onChange={e => setNewTask(prev => ({ ...prev, due_time: e.target.value }))}
                    className="w-full px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-purple-500"
                    data-testid="new-task-due-time"
                  />
                </div>

                <div>
                  <label className="block text-gray-400 text-sm mb-1">Repeat</label>
                  <select
                    value={newTask.recurrence}
                    onChange={e => setNewTask(prev => ({ ...prev, recurrence: e.target.value }))}
                    className="w-full px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-purple-500"
                    data-testid="new-task-recurrence"
                  >
                    <option value="none">No Repeat (stays done permanently)</option>
                    <option value="hourly">Hourly</option>
                    <option value="daily">Daily (auto-resets at midnight)</option>
                    <option value="weekly">Weekly</option>
                    <option value="monthly">Monthly</option>
                  </select>
                </div>

                {/* Day of Week - shown for weekly recurrence */}
                {newTask.recurrence === 'weekly' && (
                  <div>
                    <label className="block text-gray-400 text-sm mb-1">Day of Week</label>
                    <select
                      value={newTask.day_of_week}
                      onChange={e => setNewTask(prev => ({ ...prev, day_of_week: e.target.value }))}
                      className="w-full px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-purple-500"
                      data-testid="new-task-day-of-week"
                    >
                      <option value="">Select a day...</option>
                      <option value="0">Sunday</option>
                      <option value="1">Monday</option>
                      <option value="2">Tuesday</option>
                      <option value="3">Wednesday</option>
                      <option value="4">Thursday</option>
                      <option value="5">Friday</option>
                      <option value="6">Saturday</option>
                    </select>
                  </div>
                )}
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <button
                onClick={() => setShowAddModal(false)}
                className="flex-1 px-4 py-2 bg-gray-800 hover:bg-gray-700 rounded-lg text-white transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleAddTask}
                className="flex-1 px-4 py-2 bg-purple-600 hover:bg-purple-700 rounded-lg text-white transition-colors"
                data-testid="save-task-btn"
              >
                Add Task
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Task Modal */}
      {showEditModal && editingTask && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50" onClick={() => setShowEditModal(false)}>
          <div className="bg-gray-900 border border-gray-700 rounded-2xl w-[500px] p-6" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-white">Edit Task</h2>
              <button 
                onClick={() => setShowEditModal(false)}
                className="p-2 text-gray-400 hover:text-white hover:bg-gray-800 rounded-lg transition-colors"
              >
                <X size={20} />
              </button>
            </div>
            
            <div className="space-y-4">
              <div>
                <label className="block text-gray-400 text-sm mb-1">Task Title *</label>
                <input
                  type="text"
                  value={editingTask.title || ''}
                  onChange={e => setEditingTask(prev => ({ ...prev, title: e.target.value }))}
                  placeholder="Enter task title"
                  className="w-full px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-purple-500"
                  data-testid="edit-task-name"
                />
              </div>

              <div>
                <label className="block text-gray-400 text-sm mb-2">Category</label>
                <div className="flex flex-wrap gap-2" data-testid="edit-task-category-selector">
                  <button
                    type="button"
                    onClick={() => setEditingTask(prev => ({ ...prev, category: '' }))}
                    className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-all ${
                      !editingTask.category
                        ? 'bg-white/20 border-white/50 text-white'
                        : 'border-white/10 text-gray-500 hover:border-white/30 hover:text-gray-300'
                    }`}
                  >
                    None
                  </button>
                  {taskCategories.map(cat => (
                    <button
                      key={cat.id}
                      type="button"
                      onClick={() => setEditingTask(prev => ({ ...prev, category: cat.name }))}
                      className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium border transition-all ${
                        editingTask.category === cat.name
                          ? 'text-white border-transparent'
                          : 'border-white/10 text-gray-400 hover:border-white/30 hover:text-white'
                      }`}
                      style={editingTask.category === cat.name
                        ? { backgroundColor: cat.color || '#8B5CF6', borderColor: cat.color || '#8B5CF6' }
                        : {}}
                      data-testid={`edit-task-cat-${cat.id}`}
                    >
                      <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: cat.color || '#8B5CF6' }} />
                      {cat.name}
                    </button>
                  ))}
                  {taskCategories.length === 0 && (
                    <span className="text-xs text-gray-600 italic">No categories yet — create some in Manage Categories</span>
                  )}
                </div>
              </div>

              <div>
                <label className="block text-gray-400 text-sm mb-1">Description</label>
                <textarea
                  value={editingTask.description || ''}
                  onChange={e => setEditingTask(prev => ({ ...prev, description: e.target.value }))}
                  placeholder="Enter description"
                  rows={3}
                  className="w-full px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-purple-500 resize-none"
                  data-testid="edit-task-description"
                />
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-gray-400 text-sm mb-1">Est. Hours</label>
                  <input
                    type="number"
                    value={editingTask.estimated_hours || 0}
                    onChange={e => setEditingTask(prev => ({ ...prev, estimated_hours: parseInt(e.target.value) || 0 }))}
                    min={0}
                    max={100}
                    className="w-full px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-purple-500"
                    data-testid="edit-task-hours"
                  />
                </div>

                <div>
                  <label className="block text-gray-400 text-sm mb-1">Est. Minutes</label>
                  <input
                    type="number"
                    value={editingTask.estimated_minutes || 0}
                    onChange={e => setEditingTask(prev => ({ ...prev, estimated_minutes: parseInt(e.target.value) || 0 }))}
                    min={0}
                    max={59}
                    className="w-full px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-purple-500"
                    data-testid="edit-task-minutes"
                  />
                </div>

                <div>
                  <label className="block text-gray-400 text-sm mb-1">Priority</label>
                  <select
                    value={editingTask.priority || 'medium'}
                    onChange={e => setEditingTask(prev => ({ ...prev, priority: e.target.value }))}
                    className="w-full px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-purple-500"
                    data-testid="edit-task-priority"
                  >
                    <option value="high">High</option>
                    <option value="medium">Medium</option>
                    <option value="low">Low</option>
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-3 gap-4 mt-4">
                <div>
                  <label className="block text-gray-400 text-sm mb-1">Due Date</label>
                  <input
                    type="date"
                    value={editingTask.due_date || ''}
                    onChange={e => setEditingTask(prev => ({ ...prev, due_date: e.target.value }))}
                    className="w-full px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-purple-500"
                    data-testid="edit-task-due-date"
                  />
                </div>

                <div>
                  <label className="block text-gray-400 text-sm mb-1">Time</label>
                  <input
                    type="time"
                    value={editingTask.due_time || ''}
                    onChange={e => setEditingTask(prev => ({ ...prev, due_time: e.target.value }))}
                    className="w-full px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-purple-500"
                    data-testid="edit-task-due-time"
                  />
                </div>

                <div>
                  <label className="block text-gray-400 text-sm mb-1">Repeat</label>
                  <select
                    value={editingTask.recurrence || 'none'}
                    onChange={e => setEditingTask(prev => ({ ...prev, recurrence: e.target.value }))}
                    className="w-full px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-purple-500"
                    data-testid="edit-task-recurrence"
                  >
                    <option value="none">No Repeat (stays done permanently)</option>
                    <option value="hourly">Hourly</option>
                    <option value="daily">Daily (auto-resets at midnight)</option>
                    <option value="weekly">Weekly</option>
                    <option value="monthly">Monthly</option>
                  </select>
                </div>
              </div>

              {/* Day of Week - shown for weekly recurrence */}
              {editingTask.recurrence === 'weekly' && (
                <div className="mt-4">
                  <label className="block text-gray-400 text-sm mb-1">Day of Week</label>
                  <select
                    value={editingTask.day_of_week || ''}
                    onChange={e => setEditingTask(prev => ({ ...prev, day_of_week: e.target.value }))}
                    className="w-full px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-purple-500"
                    data-testid="edit-task-day-of-week"
                  >
                    <option value="">Select a day...</option>
                    <option value="0">Sunday</option>
                    <option value="1">Monday</option>
                    <option value="2">Tuesday</option>
                    <option value="3">Wednesday</option>
                    <option value="4">Thursday</option>
                    <option value="5">Friday</option>
                    <option value="6">Saturday</option>
                  </select>
                </div>
              )}
            </div>

            <div className="flex gap-3 mt-6">
              <button
                onClick={() => {
                  setShowEditModal(false);
                  setEditingTask(null);
                }}
                className="flex-1 px-4 py-2 bg-gray-800 hover:bg-gray-700 rounded-lg text-white transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveEdit}
                className="flex-1 px-4 py-2 bg-purple-600 hover:bg-purple-700 rounded-lg text-white transition-colors flex items-center justify-center gap-2"
                data-testid="save-edit-task-btn"
              >
                <Save size={18} />
                Save Changes
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Tasks;
